// « Ce qu'Alfred sait de vous » (lot 3.4) : la mémoire de l'assistant, en clair. Ce qu'il propose de retenir
// attend ici une confirmation ; ce qu'il sait se corrige ou s'oublie d'un geste ; et la personne choisit s'il
// demande avant de retenir, retient sans demander, ou ne retient rien.

import { type FormEvent, useCallback, useEffect, useState } from 'react';

import type { Actions } from '../App';
import { EnTeteRetour, Fenetre } from '../components';
import { type CategorieSouvenir, type ModeMemoire, NonConnecte, type Souvenir, api } from '../lib/api';

const CATEGORIES: [CategorieSouvenir, string, string][] = [
  ['interdit', 'À ne jamais faire', 'Alfred le respecte toujours.'],
  ['preference', 'Vos préférences', ''],
  ['fait', 'Ce qu’il sait', ''],
];

const MODES: [ModeMemoire, string, string][] = [
  ['demander', 'Me demander avant', 'Alfred propose, vous confirmez ici.'],
  ['retenir', 'Retenir sans demander', 'Ce qu’il retient apparaît directement ici ; vous pouvez l’oublier.'],
  ['rien', 'Ne rien retenir', 'Alfred n’ajoute plus rien ; ce qui est déjà là reste utilisé.'],
];

const GENRES: Record<CategorieSouvenir, string> = { interdit: 'à ne jamais faire', preference: 'préférence', fait: 'fait' };

const SOURCES: Record<string, string> = { chat: 'dans la conversation', passage: 'pendant un passage du soir', app: 'ajouté par vous' };

function Ligne({ s, actions, recharger }: { s: Souvenir; actions: Actions; recharger: () => void }) {
  const [edition, setEdition] = useState<string | null>(null);
  const faire = async (f: () => Promise<unknown>, succes: string) => {
    try {
      await f();
      actions.toast({ texte: succes });
      setEdition(null);
      recharger();
    } catch (e) {
      if (e instanceof NonConnecte) window.location.hash = '#/connexion';
      else actions.toast({ texte: (e as Error).message, erreur: true });
    }
  };

  if (edition !== null) {
    return (
      <form className="souvenir edition" onSubmit={(e) => { e.preventDefault(); void faire(() => api.changerSouvenir(s.id, { texte: edition }), 'Souvenir corrigé.'); }}>
        <input className="saisie texte" value={edition} maxLength={200} onChange={(e) => setEdition(e.target.value)} aria-label="Corriger le souvenir" autoFocus />
        <div className="boutons">
          <button className="btn principal" disabled={edition.trim().length < 3}>Enregistrer</button>
          <button className="btn" type="button" onClick={() => setEdition(null)}>Annuler</button>
        </div>
      </form>
    );
  }
  return (
    <div className="souvenir">
      <div className="t">
        <span>{s.texte}</span>
        <small className="muted">
          {/* Proposé : son genre se voit avant de confirmer (un interdit mal rangé changerait tout). */}
          {s.statut === 'propose' && `${GENRES[s.categorie]} · `}
          {SOURCES[s.source] ?? s.source} · {new Date(s.creeLe).toLocaleDateString('fr-FR')}
        </small>
      </div>
      {s.statut === 'propose' ? (
        <div className="boutons">
          <button className="btn petit principal" onClick={() => void faire(() => api.changerSouvenir(s.id, { statut: 'confirme' }), 'Alfred s’en souviendra.')}>Confirmer</button>
          <button className="btn petit" onClick={() => void faire(() => api.changerSouvenir(s.id, { statut: 'rejete' }), 'Proposition écartée.')}>Non</button>
        </div>
      ) : (
        <div className="boutons">
          <button className="btn petit" onClick={() => setEdition(s.texte)}>Corriger</button>
          <button className="btn petit" onClick={() => void faire(() => api.oublierSouvenir(s.id), 'Oublié.')}>Oublier</button>
        </div>
      )}
    </div>
  );
}

export function Memoire({ actions }: { actions: Actions }) {
  const [donnees, setDonnees] = useState<{ mode: ModeMemoire; souvenirs: Souvenir[] } | null>(null);
  const [nouveau, setNouveau] = useState<{ texte: string; categorie: CategorieSouvenir }>({ texte: '', categorie: 'preference' });

  const echec = useCallback((e: unknown) => {
    if (e instanceof NonConnecte) window.location.hash = '#/connexion';
    else actions.toast({ texte: (e as Error).message, erreur: true });
  }, [actions]);

  const charger = useCallback(() => {
    api.souvenirs().then(setDonnees).catch(echec);
  }, [echec]);

  useEffect(charger, [charger]);

  const ajouter = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.ajouterSouvenir(nouveau.texte.trim(), nouveau.categorie);
      setNouveau({ ...nouveau, texte: '' });
      actions.toast({ texte: 'Alfred s’en souviendra.' });
      charger();
    } catch (err) {
      echec(err);
    }
  };

  const regler = async (mode: ModeMemoire) => {
    try {
      await api.reglerMemoire(mode);
      setDonnees((d) => (d ? { ...d, mode } : d));
    } catch (err) {
      echec(err);
    }
  };

  const proposes = donnees?.souvenirs.filter((s) => s.statut === 'propose') ?? [];
  const confirmes = donnees?.souvenirs.filter((s) => s.statut === 'confirme') ?? [];

  return (
    <main className="ecran">
      <EnTeteRetour titre="Mémoire" retour="#/reglages" />
      <p className="message">Ce qu’Alfred sait de vous</p>
      {!donnees && <p className="message muted">Chargement…</p>}

      {proposes.length > 0 && (
        <Fenetre>
          <span className="display" style={{ fontWeight: 500 }}>À confirmer</span>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Alfred ne s’en servira qu’une fois confirmé.</p>
          {proposes.map((s) => <Ligne key={s.id} s={s} actions={actions} recharger={charger} />)}
        </Fenetre>
      )}

      {donnees && confirmes.length === 0 && proposes.length === 0 && (
        <p className="muted" style={{ textAlign: 'center' }}>Alfred ne sait encore rien de particulier sur vous. Dites-lui « retiens que… » dans la conversation, ou ajoutez-le ci-dessous.</p>
      )}

      {CATEGORIES.map(([cat, titre, aide]) => {
        const liste = confirmes.filter((s) => s.categorie === cat);
        if (!liste.length) return null;
        return (
          <section key={cat} className="dossier">
            <h2 className="dossier-titre">{titre} <span>{liste.length}</span></h2>
            {aide && <p className="muted" style={{ margin: 0, fontSize: 12 }}>{aide}</p>}
            <div className="dossier-notes">
              {liste.map((s) => <Ligne key={s.id} s={s} actions={actions} recharger={charger} />)}
            </div>
          </section>
        );
      })}

      <Fenetre>
        <span className="display" style={{ fontWeight: 500 }}>Ajouter</span>
        <form className="formulaire" onSubmit={(e) => void ajouter(e)}>
          <input className="saisie texte" placeholder="Préfère le thé au café" value={nouveau.texte} maxLength={200} onChange={(e) => setNouveau({ ...nouveau, texte: e.target.value })} aria-label="Ce qu’Alfred doit savoir" />
          <div className="boutons">
            <select className="saisie texte" value={nouveau.categorie} onChange={(e) => setNouveau({ ...nouveau, categorie: e.target.value as CategorieSouvenir })} aria-label="Genre">
              <option value="preference">Préférence</option>
              <option value="fait">Fait</option>
              <option value="interdit">À ne jamais faire</option>
            </select>
            <button className="btn principal" disabled={nouveau.texte.trim().length < 3}>Ajouter</button>
          </div>
        </form>
      </Fenetre>

      {donnees && (
        <Fenetre>
          <span className="display" style={{ fontWeight: 500 }}>Quand Alfred apprend en discutant</span>
          <div className="choix-mode" role="radiogroup" aria-label="Mode de la mémoire">
            {MODES.map(([mode, libelle, aide]) => (
              <button key={mode} className="btn" role="radio" aria-checked={donnees.mode === mode} onClick={() => void regler(mode)}>
                <b>{libelle}</b>
                <small>{aide}</small>
              </button>
            ))}
          </div>
        </Fenetre>
      )}
    </main>
  );
}
